import { ListModel, ListModelWithIntValue, ListModelWithType, TranslationList } from "shared/interfaces/core-interfaces";

// The slice holds only what several components share: the pending count per module, which both
// the sidebar badges and the default tab of a module read. A module's own request lists live in
// RequestTypeView, the only screen that shows them.
export interface RequestSliceModel {
    // Pending count per module slug, from the aggregate api/requests/pending call; null until that
    // call has answered once, so "not loaded yet" is not mistaken for "nothing pending".
    pendingCountsBySlug: Record<string, number> | null,
}

export const initialRequestState: RequestSliceModel = {
    pendingCountsBySlug: null,
}

// Single row model covering every request type returned by `api/requests/get`.
// Base fields (RequestBaseDTO) are always present; type-specific fields are optional.
export interface TypedRequestRow {
    // base fields
    id: string,
    entityId: string | null,
    createdAt: Date,
    resolvedAt: Date | null,
    requestedById: string,
    requestedBy: string,
    typeId: number,
    type: string,
    requestType?: string, // display-only: translated `type`, added when mapping rows for the shared tables.
    tenantId: string | null,
    tenant: string,
    status: string,
    approvalList: string,
    respondedBy: string[],
    requestResponses: RequestResponses[],
    hasPermission: boolean,
    hasLicence: boolean,

    // shared by several types
    from?: Date | string | null,
    to?: Date | string | null,
    currency?: string | null,
    description?: string | null,
    comment?: string | null,

    // absence
    requestedHours?: number,
    requestedDays?: number,

    // refund
    supplier?: string | null,
    sum?: number,
    sumWithoutTax?: number,

    // bonus & commission / salary deduction
    employeeId?: string,
    employee?: string,
    amount?: number,
    paymentInterval?: string | null,

    // financial document
    clientSupplier?: string | null,
    costCenter?: string | null,
    net?: number,
    gross?: number,
    paymentDeadline?: Date | null,
    documentDate?: Date | null,
    documentNumber?: string | null,
    paymentMethod?: TranslationList | null,

    // travel plan
    traveler?: string | null,
    startDate?: Date | null,
    endDate?: Date | null,
    reasonForTravel?: string | null,

    // month reopen
    year?: number,
    month?: number,
}

// Response of GET api/requests/get?requestTypeId=&includeAll= (GetRequestsResponse<T>).
export interface GetTypedRequestsResponse {
    pendingRequests: TypedRequestRow[];
    myRequests: TypedRequestRow[];
    allRequests: TypedRequestRow[];
}

// Response of GET api/requests/pending (GetPendingRequestsResponse). Only the type id is
// needed here — the sidebar count pill just groups the pending requests by module.
export interface PendingCountRow {
    requestTypeId: number;
}

export interface GetPendingRequestsResponse {
    requests: PendingCountRow[];
}

export interface RequestResponses {
    status: "Approved" | "Declined",
    date?: Date | null,
    fullName: string,
    comment?: string | null,
}

// Used by finance overdue calculations in shared/utils and finance-document-interfaces.
export interface RequestForOverdueCalculation {
    id: string,
    statusName: string,
    createdDate: Date,
    resolvedDate: Date | null
}

export interface RejectRequestRequest {
    requestId: string;
    description: string;
}

export interface RejectRequestBatchRequest {
    requestIds: string[];
    description: string;
}

export interface ApproveRequestRequest {
    requestId: string;
    description: string;
}

export interface ApproveRequestBatchRequest {
    requestIds: string[];
    description: string;
}

export interface RequestDetailsResponse {
    tenant: string;
    clientSupplier: string;
    currency: string;
    net: number;
    gross: number;
    costCenter: string;
    paymentDeadline?: Date | null;
    documentDate?: Date | null;
    documentNumber?: string | null;
    hasDetails: boolean

    files: ListModel<string>[];
    documents: ListModelWithIntValue<string>[];
    previousDocuments: ListModelWithType<string>[];
}
